/**
 * OvertimeSettings — Overtime thresholds, break rules, holiday dates, RPLH target.
 * All state and handlers live in Settings.js (parent). This component is pure JSX.
 * RULE: Do NOT add state here. Pass new props from Settings.js instead.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { DollarSign, Plus, X, Loader2 } from 'lucide-react';

export default function OvertimeSettings({
  payrollSettings,
  setPayrollSettings,
  payrollSaving,
  savePayrollSettings,
  addHolidayDate,
  removeHolidayDate,
  updateHolidayDate,
  newHolidayIdx,
  newHolidayNameRef,
}) {
  return (
    /* data-settings-section="overtime" (REQUIRED — do not remove) */
    <Card className="border-slate-200" data-testid="payroll-settings-card" data-settings-section="overtime">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <DollarSign size={20} className="text-slate-600" />
          Overtime &amp; Holiday Pay
        </CardTitle>
        <CardDescription>Configure overtime thresholds and holiday pay multipliers for payroll calculations</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overtime fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">Daily Overtime After (hours)</Label>
            <Input
              type="number"
              min="1"
              max="24"
              step="0.5"
              value={payrollSettings.overtime_after_hours}
              onChange={e => setPayrollSettings(prev => ({ ...prev, overtime_after_hours: parseFloat(e.target.value) || 8 }))}
              data-testid="ot-after-hours-input"
            />
            <p className="text-xs text-slate-500">Hours per day before overtime applies (default: 8)</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">Overtime Multiplier (×)</Label>
            <Input
              type="number"
              min="1"
              max="5"
              step="0.25"
              value={payrollSettings.overtime_multiplier}
              onChange={e => setPayrollSettings(prev => ({ ...prev, overtime_multiplier: parseFloat(e.target.value) || 1.25 }))}
              data-testid="ot-multiplier-input"
            />
            <p className="text-xs text-slate-500">Multiplier applied to overtime hours (default: 1.25×)</p>
          </div>
        </div>

        {/* Break rule */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium text-slate-700">Automatic Break Deduction</Label>
              <p className="text-xs text-slate-500 mt-0.5">
                Deducts unpaid break time from payable hours before overtime calculation
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={payrollSettings.break_rule?.enabled ?? true}
              onClick={() =>
                setPayrollSettings(prev => ({
                  ...prev,
                  break_rule: {
                    ...(prev.break_rule || { hours_per_block: 4, minutes_per_block: 30 }),
                    enabled: !(prev.break_rule?.enabled ?? true),
                  },
                }))
              }
              className="relative shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 rounded-full"
              style={{ width: 44, height: 24 }}
              data-testid="break-rule-toggle"
            >
              <span
                className={`absolute inset-0 rounded-full transition-colors duration-200 ${
                  (payrollSettings.break_rule?.enabled ?? true) ? 'bg-orange-500' : 'bg-slate-200'
                }`}
              />
              <span
                className="absolute top-[2px] left-[2px] rounded-full bg-white shadow transition-transform duration-200"
                style={{
                  width: 20,
                  height: 20,
                  transform: (payrollSettings.break_rule?.enabled ?? true)
                    ? 'translateX(20px)'
                    : 'translateX(0px)',
                }}
              />
            </button>
          </div>
          {(payrollSettings.break_rule?.enabled ?? true) && (
            <div className="grid grid-cols-2 gap-4 max-w-sm pl-1">
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Break Duration (minutes)</Label>
                <Input
                  type="number"
                  min="5"
                  max="120"
                  step="5"
                  value={payrollSettings.break_rule?.minutes_per_block ?? 30}
                  onChange={e =>
                    setPayrollSettings(prev => ({
                      ...prev,
                      break_rule: {
                        ...(prev.break_rule || { enabled: true, hours_per_block: 4 }),
                        minutes_per_block: parseInt(e.target.value) || 30,
                      },
                    }))
                  }
                  data-testid="break-minutes-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Per Every (hours)</Label>
                <Input
                  type="number"
                  min="1"
                  max="12"
                  step="0.5"
                  value={payrollSettings.break_rule?.hours_per_block ?? 4}
                  onChange={e =>
                    setPayrollSettings(prev => ({
                      ...prev,
                      break_rule: {
                        ...(prev.break_rule || { enabled: true, minutes_per_block: 30 }),
                        hours_per_block: parseFloat(e.target.value) || 4,
                      },
                    }))
                  }
                  data-testid="break-hours-input"
                />
              </div>
              <p className="col-span-2 text-xs text-slate-400">
                Example: {payrollSettings.break_rule?.minutes_per_block ?? 30} min break per{' '}
                {payrollSettings.break_rule?.hours_per_block ?? 4}h worked — an 8h shift pays{' '}
                {(8 - Math.floor(8 * 60 / ((payrollSettings.break_rule?.hours_per_block ?? 4) * 60)) * (payrollSettings.break_rule?.minutes_per_block ?? 30) / 60).toFixed(1)}h
              </p>
            </div>
          )}
        </div>

        {/* Holiday dates list */}
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <Label className="text-sm font-medium text-slate-700">Holiday Dates</Label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addHolidayDate}
              data-testid="add-holiday-btn"
              className="w-full sm:w-auto h-9 text-sm border-slate-300 hover:bg-slate-50"
            >
              <Plus size={15} className="mr-1.5" /> Add Holiday
            </Button>
          </div>
          {payrollSettings.holiday_dates.length === 0 && (
            <div className="flex flex-col items-center justify-center py-6 border border-dashed border-slate-200 rounded-lg bg-slate-50">
              <p className="text-sm font-medium text-slate-500">No holidays added yet</p>
              <p className="text-xs text-slate-400 mt-0.5">Click &quot;Add Holiday&quot; to configure holiday pay dates</p>
            </div>
          )}
          <div className="space-y-2">
            {payrollSettings.holiday_dates.map((hd, idx) => {
              const isNew = idx === newHolidayIdx;
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-2 flex-wrap sm:flex-nowrap rounded-lg p-1.5 -mx-1.5 transition-all duration-500 ${
                    isNew ? 'bg-orange-50 ring-1 ring-orange-200 animate-fade-in' : 'bg-transparent'
                  }`}
                  data-testid={`holiday-row-${idx}`}
                >
                  <Input
                    ref={isNew ? newHolidayNameRef : null}
                    placeholder="Holiday name"
                    value={hd.name}
                    onChange={e => updateHolidayDate(idx, 'name', e.target.value)}
                    className="flex-1 min-w-[130px]"
                    data-testid={`holiday-name-${idx}`}
                  />
                  <Input
                    type="date"
                    value={hd.date}
                    onChange={e => updateHolidayDate(idx, 'date', e.target.value)}
                    className="w-40"
                    data-testid={`holiday-date-${idx}`}
                  />
                  <select
                    value={hd.holiday_type || 'regular'}
                    onChange={e => updateHolidayDate(idx, 'holiday_type', e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-ring w-44"
                    data-testid={`holiday-type-${idx}`}
                  >
                    <option value="regular">Regular (2.0×)</option>
                    <option value="non_working">Non-working (1.3×)</option>
                  </select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0 text-slate-400 hover:text-red-500 shrink-0"
                    onClick={() => removeHolidayDate(idx)}
                    data-testid={`remove-holiday-${idx}`}
                  >
                    <X size={15} />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        {/* RPLH Target */}
        <div className="border-t border-slate-100 pt-5">
          <div className="max-w-xs space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">RPLH Target ($ per hour)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
              <Input
                type="number"
                min="0"
                step="0.5"
                placeholder="e.g. 45"
                value={payrollSettings.rplh_target}
                onChange={e => setPayrollSettings(prev => ({ ...prev, rplh_target: e.target.value }))}
                className="pl-7"
                data-testid="rplh-target-input"
              />
            </div>
            <p className="text-xs text-slate-500">
              Optional. When set, each payroll run shows whether RPLH is on or below target.
              Leave blank to disable comparison.
            </p>
          </div>
        </div>

        <Button
          onClick={savePayrollSettings}
          disabled={payrollSaving}
          data-testid="save-payroll-settings-btn"
          className="h-9"
        >
          {payrollSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {payrollSaving ? 'Saving...' : 'Save Payroll Settings'}
        </Button>
      </CardContent>
    </Card>
  );
}
